// Ported from dev-scrips-pm-react src/billing/components/void-payment-dialog.tsx (PROD-3312 appointment Billing tab,
// step 5). "Void this payment?": a required reason from GET /Billing/Payment/GetPaymentVoidReasons (fetched only while
// open), then PUT /Billing/Payment/Void/{receiptId}[?linkedReceiptId=] with `{ voidReason: { id, value }, voidedById }`.
// A "Cashier's shift is not open" failure closes this dialog and hands off to the host (`onCashierShiftRequired`), with
// no retry; any other failure keeps it open under the global toast.
// Differences from pm-react, none of them behavioural:
//   - the form is its own component, mounted only while the dialog is open, so every open starts with no reason chosen
//     and a fresh mutation. pm-react got the same from an open-gated effect that reset both.
//   - PM primitives come from the PROD-3310 billing bridge; the reason trigger gets an aria-label (pm-react's <label>
//     is not associated with it); the bridge Select shows the chosen reason's label itself, so pm-react's
//     `SelectValue` function child is not needed.
//   - the destructive button is `bg-status-error text-white` (pm-react relies on the primary button's white text).
import { useState } from 'react';
import {
  BridgeButton as Button,
  BridgeSelect as Select,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/billing/bridge';
import { useAuthStore } from '@/auth/auth-store';
import { useVoidPayment, useVoidReasons } from '@/billing/services/use-payments';
import { handlePaymentError } from '@/billing/lib/payment-errors';
import { REASON_SELECT_CONTENT_CLASS } from '@/billing/lib/reason-select';
import type { PaymentRow } from '@/shared/types/billing';

interface VoidPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: PaymentRow;
  invoiceId?: string;
  /** Cashier-shift escalation: see `handlePaymentError`. */
  onCashierShiftRequired?: () => void;
}

export function VoidPaymentDialog({ open, ...rest }: VoidPaymentDialogProps) {
  return open ? <VoidPaymentForm {...rest} /> : null;
}

function VoidPaymentForm({
  onOpenChange,
  payment,
  invoiceId,
  onCashierShiftRequired,
}: Omit<VoidPaymentDialogProps, 'open'>) {
  const userId = useAuthStore((s) => s.user?.userId);
  const voidPayment = useVoidPayment();
  const reasonsQuery = useVoidReasons(true);
  const reasons = reasonsQuery.data ?? [];

  // Selected reason id; the matching {id,value} is shipped verbatim.
  const [reasonId, setReasonId] = useState('');
  const selectedReason = reasons.find((r) => r.id === reasonId);

  const submit = async () => {
    if (!selectedReason || !payment.id) return;
    try {
      await voidPayment.mutateAsync({
        receiptId: payment.id,
        linkedReceiptId: payment.linkedReceiptId ?? undefined,
        invoiceId,
        request: {
          voidReason: { id: selectedReason.id, value: selectedReason.value },
          voidedById: userId,
        },
      });
      onOpenChange(false);
    } catch (err) {
      if (handlePaymentError(err, onCashierShiftRequired) === 'handled') {
        onOpenChange(false);
        return;
      }
      /* Global toast surfaces failure. */
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Void this payment?</DialogTitle>
          <DialogDescription>
            Voiding receipt <span className="font-medium text-text-primary">{payment.receiptNumber ?? payment.id}</span>{' '}
            removes its contribution to the invoice balance. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 px-1">
          <label className="text-xs font-medium text-text-muted">Void reason</label>
          <Select value={reasonId} onValueChange={(v) => setReasonId(v ?? '')}>
            <SelectTrigger className="w-full" aria-label="Void reason">
              <SelectValue placeholder={reasonsQuery.isLoading ? 'Loading reasons…' : 'Select a reason'} />
            </SelectTrigger>
            <SelectContent className={REASON_SELECT_CONTENT_CLASS}>
              {reasons.map((r) => (
                <SelectItem key={r.id} value={r.id ?? ''}>
                  {r.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={voidPayment.isPending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!selectedReason || voidPayment.isPending}
            className="bg-status-error text-white hover:bg-status-error/90"
          >
            {voidPayment.isPending ? 'Voiding…' : 'Void payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
